/**
 * What one cell of a spreadsheet artifact's preview grid shows.
 *
 * A cell in the xlsx / numbers model is a scalar, or a dressed object
 * {v, bold, italic, fill, color, format, align}. A string starting with "="
 * is a formula: the grid shows the value the server computed for it
 * (`formula_values` on the artifact detail, backend formula_values.py) — as
 * Excel, Sheets, ChatGPT and Claude do — with the formula itself on hover. A
 * formula the server could not compute shows its own text, muted.
 */

import type { FormulaValues } from '@/types/models';

export const cellValue = (cell: any): any =>
  cell !== null && typeof cell === 'object' ? cell.v : cell;

export const isFormula = (v: any): v is string =>
  typeof v === 'string' && v.trim().startsWith('=');

/**
 * A number as Excel's General format shows it in a default-width column: at
 * most 11 characters, so 0.942857142857143 reads 0.942857143 and binary noise
 * (0.30000000000000004) never shows; past that, scientific (1.23457E+11).
 */
export const generalNumber = (n: number): string => {
  if (!Number.isFinite(n)) return String(n);
  const abs = Math.abs(n);
  if (Number.isInteger(n) && abs < 1e11) return String(n);
  if (abs !== 0 && (abs >= 1e11 || abs < 1e-9)) {
    const [mantissa, exponent] = n.toExponential(5).split('e');
    return `${Number(mantissa)}E${exponent}`;
  }
  for (let digits = 10; digits > 0; digits -= 1) {
    const text = String(Number(n.toPrecision(digits)));
    if (text.length <= 11) return text;
  }
  return String(n);
};

export const displayText = (v: any): string => {
  if (v === null || v === undefined) return '';
  if (typeof v === 'number') return generalNumber(v);
  if (typeof v === 'boolean') return v ? 'TRUE' : 'FALSE';
  return String(v);
};

const ERROR_VALUE = /^#(?:DIV\/0!|N\/A|NAME\?|NULL!|NUM!|REF!|VALUE!|SPILL!|CALC!)$/;

export interface ShownCell {
  text: string;
  /** The formula, shown on hover over its value. */
  title?: string;
  /** is-num (right-aligned), is-center (TRUE / #DIV/0!), is-formula (not computed). */
  className?: 'is-num' | 'is-center' | 'is-formula';
  /**
   * Read left to right whatever the UI direction: a number, an error and a
   * formula are not words - in a Hebrew grid "#DIV/0!" would otherwise read
   * "!DIV/0#" and -5 would read "5-".
   */
  ltr?: boolean;
}

/**
 * The cell at model row `row`, column `col` of sheet `sheet` (all 0-based,
 * the header row included — the server's key is "<row>:<col>").
 */
export const shownCell = (
  cell: any,
  values: FormulaValues | null | undefined,
  sheet: number,
  row: number,
  col: number,
): ShownCell => {
  const v = cellValue(cell);
  if (!isFormula(v)) {
    return typeof v === 'number'
      ? { text: displayText(v), className: 'is-num', ltr: true }
      : { text: displayText(v), className: undefined };
  }
  const computed = values?.[sheet];
  const key = `${row}:${col}`;
  if (computed && Object.prototype.hasOwnProperty.call(computed, key)) {
    const value = computed[key];
    if (typeof value === 'number') return { text: displayText(value), title: v, className: 'is-num', ltr: true };
    if (typeof value === 'string' && ERROR_VALUE.test(value)) {
      return { text: value, title: v, className: 'is-center', ltr: true };
    }
    if (typeof value === 'boolean') return { text: displayText(value), title: v, className: 'is-center' };
    return { text: displayText(value), title: v, className: undefined };
  }
  return { text: v, className: 'is-formula', ltr: true };
};
